/**
 * Deployment Type Narrowing Utilities
 *
 * Type-safe helper functions for working with deployment responses.
 * These functions provide type narrowing based on the deployment type.
 */

import { DbEnum } from "../../../generated/prisma-enums";
import type { TApiDeployments } from "./index";
import type { TypedDeploymentResponse } from "./schemas/deployment.types";

/**
 * Narrow deployment response to specific deployment type
 *
 * Type-safe narrowing function that extracts a specific deployment type
 * from the base API response. Returns null if the deployment type doesn't match.
 *
 * Uses DbEnum.DeploymentType (database source of truth) to ensure type safety.
 * This is the single point of type conversion at the API boundary.
 *
 * Function overloads ensure TypeScript properly narrows the return type based on
 * the literal type value passed, without requiring any casts.
 *
 * @example
 * ```typescript
 * const response = await api.deployments.get({ id })
 * const excelDeployment = narrow(DbEnum.DeploymentType.EXCEL_ADDIN, response)
 *
 * if (excelDeployment) {
 *   // TypeScript knows deploymentSettings is ExcelDeploymentSettings!
 *   excelDeployment.deploymentSettings.functionName // ✅ No cast!
 * }
 * ```
 *
 * @param type - The deployment type from DbEnum.DeploymentType
 * @param deployment - The base API response (from TApiDeployments["Create"]["Response"])
 * @returns The narrowed typed response, or null if types don't match
 */
export function narrow(
  type: DbEnum.DeploymentType.EXCEL_ADDIN,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.EXCEL_ADDIN }> | null;
export function narrow(
  type: DbEnum.DeploymentType.SLACK,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.SLACK }> | null;
export function narrow(
  type: DbEnum.DeploymentType.TWILIO_SMS,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.TWILIO_SMS }> | null;
export function narrow(
  type: DbEnum.DeploymentType.FB_MESSENGER,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.FB_MESSENGER }> | null;
export function narrow(
  type: DbEnum.DeploymentType.WHATSAPP,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.WHATSAPP }> | null;
export function narrow(
  type: DbEnum.DeploymentType.TEAMS,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.TEAMS }> | null;
export function narrow(
  type: DbEnum.DeploymentType.TELEGRAM,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.TELEGRAM }> | null;
export function narrow(
  type:
    | DbEnum.DeploymentType.GMAIL
    | DbEnum.DeploymentType.OUTLOOK
    | DbEnum.DeploymentType.IMAP
    | DbEnum.DeploymentType.SMTP,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<
  TypedDeploymentResponse,
  {
    type:
      | DbEnum.DeploymentType.GMAIL
      | DbEnum.DeploymentType.OUTLOOK
      | DbEnum.DeploymentType.IMAP
      | DbEnum.DeploymentType.SMTP;
  }
> | null;
export function narrow(
  type: DbEnum.DeploymentType.EMBED,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.EMBED }> | null;
export function narrow(
  type: DbEnum.DeploymentType.TWILIO_VOICE,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.TWILIO_VOICE }> | null;
export function narrow(
  type: DbEnum.DeploymentType.GOOGLE_SHEETS,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.GOOGLE_SHEETS }> | null;
export function narrow(
  type: DbEnum.DeploymentType.WORD_ADDIN,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.WORD_ADDIN }> | null;
export function narrow(
  type: DbEnum.DeploymentType.POWERPOINT_ADDIN,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.POWERPOINT_ADDIN }> | null;
export function narrow(
  type: DbEnum.DeploymentType.OUTLOOK_ADDIN,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.OUTLOOK_ADDIN }> | null;
export function narrow(
  type: DbEnum.DeploymentType.API,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): Extract<TypedDeploymentResponse, { type: DbEnum.DeploymentType.API }> | null;
export function narrow(
  type: DbEnum.DeploymentType,
  deployment: TApiDeployments["Create"]["Response"]["data"]
): TypedDeploymentResponse | null {
  if (deployment.type !== type) {
    return null;
  }
  // Runtime check ensures type matches - TypeScript narrows via overloads above
  // After runtime check, we know deployment matches the expected type
  // Use constrained type assertion instead of double cast
  return deployment as Extract<TypedDeploymentResponse, { type: typeof type }>;
}
